import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { BottomNavigation, BottomNavigationAction, Box } from '@mui/material';
import { Crosshair, Shuffle, Table } from '@phosphor-icons/react';
import { getIsMobileApp } from '../../utils/mobileApp';
import { DEFAULT_PROPS_LEAGUE } from '../stokasticProps/data/leagueConfig';

const BOTTOM_NAV_HEIGHT = 64;

// Drawers/modals that must render above the native-app bottom nav.
export const ABOVE_BOTTOM_NAV_ZINDEX = 1400;

type NavTab = 'props' | 'datahub' | 'lineupsim';

function getLeagueFromPath(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  return segments.length >= 2 ? segments[1] : DEFAULT_PROPS_LEAGUE;
}

function getActiveTab(pathname: string): NavTab | null {
  if (pathname.startsWith('/props')) return 'props';
  if (pathname.startsWith('/datahub')) return 'datahub';
  if (pathname.startsWith('/singlelineupsim')) return 'lineupsim';
  return null;
}

const MobileAppBottomNav: React.FC = () => {
  const location = useLocation();
  const history = useHistory();

  const isMobileApp = getIsMobileApp();
  if (!isMobileApp) return null;

  const activeTab = getActiveTab(location.pathname);
  if (activeTab === null) return null;

  const league = getLeagueFromPath(location.pathname);

  const handleChange = (_: React.SyntheticEvent, newValue: NavTab) => {
    localStorage.setItem('mobileLastTab', newValue);
    if (newValue === 'props') history.push(`/props/${league}`);
    else if (newValue === 'datahub') history.push(`/datahub/${league}`);
    else if (newValue === 'lineupsim') history.push(`/singlelineupsim/${league}`);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1300,
        height: BOTTOM_NAV_HEIGHT,
        borderTop: '1px solid rgba(255, 255, 255, 0.12)',
      }}
    >
      <BottomNavigation
        value={activeTab}
        onChange={handleChange}
        showLabels
        sx={{
          height: '100%',
          padding: 0,
          backgroundColor: '#161617',
          borderTop: 'none !important',
          '& .MuiBottomNavigationAction-root': {
            color: 'rgba(242, 242, 243, 0.7)',
            flex: 1,
            minWidth: 0,
            maxWidth: 'none',
            padding: '8px 0',
            position: 'relative',
            overflow: 'visible',
            '&.Mui-selected': {
              color: 'rgba(242, 242, 243, 0.7)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                backgroundColor: '#42a5f5',
              },
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '9.25px',
            fontWeight: 400,
            lineHeight: 1,
            '&.Mui-selected': {
              fontSize: '9.25px',
              fontWeight: 500,
            },
          },
        }}
      >
        <BottomNavigationAction
          label="Props"
          value="props"
          icon={<Crosshair size={30} weight="thin" />}
        />
        <BottomNavigationAction
          label="Data Hub"
          value="datahub"
          icon={<Table size={30} weight="thin" />}
        />
        <BottomNavigationAction
          label="SL Sim"
          value="lineupsim"
          icon={<Shuffle size={30} weight="thin" />}
        />
      </BottomNavigation>
    </Box>
  );
};

export default MobileAppBottomNav;
